// 3rd party imports
import * as React from 'react';
import { useHistory, Link, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';

import { inject } from 'mobx-react';

import { AnalyticsStore } from 'bernie-plugin-mobx';
import { mergeQueryString } from 'bernie-core';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import { ILocalization, LocalizedText } from '@shared-ui/localization-context';
import { useLogger } from '@shared-ui/logger-context';

import { PzStore, PzCampaignStore, UserAuthStore } from 'stores';

import { Program } from 'models';

import { UitkLoader } from 'uitk-react-loader';
import { UitkPrimaryButton, UitkTertiaryButton, UitkButtonSize } from 'uitk-react-button';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkEmailInput, UitkPasswordInput, UitkSelect, UitkTextInput } from 'uitk-react-input';
import { UitkSpacing, UitkSpacingHr } from 'uitk-react-spacing';
import { UitkMoreInfoTrigger } from 'uitk-react-more-info-trigger';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkText, UitkHeading } from 'uitk-react-text';
import { UitkLink } from 'uitk-react-link';
import { UitkEmptyState, UitkEmptyStateActionArea } from 'uitk-react-empty-state';
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkCheckbox, UitkCheckboxGroup } from 'uitk-react-switch';

import { PzFormInfoDialog } from 'components/signup/pz-form-info-dialog';

import { LocalizedTextWrapping } from 'components/localized-text-wrapping';

import { getUserAnalyticsMicroMessage, analyticsEvent } from '../../util/analytics';
import updateUserProgramRelationship from 'components/signup/helper-fn/program-relationship-update-handler';

// util
import urlFormatter from '../../util/url-formatter';

interface SignupPzFormProps {
  analytics: AnalyticsStore;
  l10n?: ILocalization;
  pzStore?: PzStore;
  pzCampaignStore?: PzCampaignStore;
  countries?: Map<string, string>;
  program?: Program;
  userAuth?: UserAuthStore;
  terms?: string;
  termsId?: string;
  termsState?: string;
  campaignTerms?: { terms: string; terms_id: string; state: string };
  locale: string;
}

export const SignupPzForm: React.FunctionComponent<SignupPzFormProps> = inject('pzStore')(
  ({
    analytics,
    l10n,
    pzStore,
    pzCampaignStore,
    countries,
    program,
    userAuth,
    terms,
    termsId,
    termsState,
    campaignTerms,
    locale,
  }) => {
    const LOGGER = useLogger();
    LOGGER.info('affhub.src.components.join-program-form');

    const history = useHistory();
    const location = useLocation();

    const [checkboxValue, setCheckboxValue] = React.useState({ pzTc: false, programTc: false });
    const [infoEntry, setInfoEntry] = React.useState('');
    const [formError, setFormError] = React.useState(null);
    const [email, setEmail] = React.useState(null);
    const [status, setStatus] = React.useState(null);
    const [camRef, setCamRef] = React.useState(null);
    const [partnerID, setPartnerID] = React.useState(null);

    const {
      control,
      formState: { errors },
      handleSubmit,
      watch,
    } = useForm({ mode: 'onBlur' });

    const user = userAuth?.userDetails?.body;

    const pzSignupUsername = l10n.formatText('pz.signup.form.label.username');
    const pzSignupPassword = l10n.formatText('pz.signup.form.label.password');
    const pzSignupPostcode = l10n.formatText('pz.signup.form.label.postcode');
    const pzSignupAccountName = l10n.formatText('pz.signup.form.label.account_name');
    const pzSignupAddress = l10n.formatText('pz.signup.form.label.address');
    const pzSignupSubmitLabel = l10n.formatText('pz.signup.form.label.submit');
    const pzCountryLabel = l10n.formatText('pz.signup.form.label.country');
    const pzSignupPhone = l10n.formatText('pz.signup.form.label.phone');
    const pzTermsAndConditionsLabel = l10n.formatText('pz.signup.form.terms_and_conditions.pz_terms');
    const programTermsAndConditionsLabel = l10n.formatText(
      'pz.signup.form.terms_and_conditions.program_terms',
      program.title,
    );

    const emailMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_email');
    const passwordMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_password');
    const countryMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_country');
    const usernameMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_username');
    const accountNameMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_account_name');
    const addressMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_address');
    const postcodeMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_postcode');
    const phoneMissingErrorlabel = l10n.formatText('pz.signup.form.message.no_phone');
    const pzTermsAndConditionsMissingErrorLabel = l10n.formatText(
      'pz.signup.form.message.terms_and_conditions.please_accept',
    );

    const emailLabel = l10n.formatText('pz.signup.form.label.email');
    const emailInvalidErrorLabel = l10n.formatText('pz.signup.message.invalid_email');
    const confirmPasswordLabel = l10n.formatText('pz.signup.form.label.confirm_password');
    const confirmPasswordMissingErrorLabel = l10n.formatText('pz.signup.form.message.reenter_your_password');
    const confirmPasswordMismatchLabel = l10n.formatText('pz.signup.form.message.confirm_your_password');

    const rfrrIdPzAccountCreated = 'affhub_pz_account.created';
    const linkNamePzAccountCreated = 'AffHub PZ Account Created';

    // delete
    const rfrrIdProgramJoinApproved = 'affhub_program_join.approved';
    const linkNameProgramJoinApproved = 'AffHub Program Join Approved';

    // error labels
    const userAccountExistsErrorLabel = l10n.formatText('pz.signup.error.username_exists');
    const accountNameExistsErrorLabel = l10n.formatText('pz.signup.error.account_exists');
    const unknownServerErrorLabel = l10n.formatText('pz.signup.error.unknown_server_error');

    const analyticsTrackEvents = (rfrr: string) => {
      switch (rfrr) {
        case rfrrIdPzAccountCreated:
          analyticsEvent(analytics, userAuth, pzStore, rfrr, linkNamePzAccountCreated);
          break;
        case rfrrIdProgramJoinApproved:
          analyticsEvent(analytics, userAuth, pzStore, rfrr, linkNameProgramJoinApproved);
          break;

        default:
          break;
      }
    };

    const redirectToConfirmation = (camref, partnerid, existingRegistration, username) => {
      const newLocation = {
        pathname: `/${locale}/join-program-confirmation`,
        state: {
          camRef: `${camref}`,
          partnerId: `${partnerid}`,
          username,
          existingRegistration,
          program: {
            name: program.name,
            title: program.title,
            locale: program.locale,
            networkTrackingBaseUrl: program.networkTrackingBaseUrl,
            siteUrl: program.siteUrl,
            creativeRef: program.creativeRef,
          },
        },
      };
      history.push(newLocation);
    };

    const triggerSignup = async (fields) => {
      await pzStore.createUser(program.name, fields).then(async (response) => {
        setStatus(null);
        setFormError(null);
        setCamRef(null);
        setPartnerID(null);

        if (response.success) {
          analyticsTrackEvents(rfrrIdPzAccountCreated);
          analyticsTrackEvents(rfrrIdProgramJoinApproved);
          const pid = response.body.partnerId;
          const camref = response.body.camref;
          const accName = fields.accountName;
          const username = fields.username;

          await updateUserProgramRelationship(userAuth, program, pid, camref, accName);
          redirectToConfirmation(camref, pid, false, username);

          LOGGER.event(new SystemEvent(SystemEventLevel.INFO, 'PZ_SIGNUP_FLOW_SUCCESS'));
        } else {
          handleErrorResponse(response);
        }
      });
    };

    const onSubmit = handleSubmit((fields) => {
      if (fields.accountName.includes(fields.username)) {
        setFormError(accountNameRules.get('cannot_contain_username'));

        return;
      }
      if (fields.username.includes(fields.accountName)) {
        setFormError(usernameRules.get('cannot_contain_accountname'));

        return;
      }

      fields.pzTermsId = termsId;
      fields.campaignTermsId = campaignTerms.terms_id;

      fields.firstName = user.firstName;
      fields.lastName = user.lastName;
      fields.companyName = user.company;

      // check format of website url
      fields.website = urlFormatter(user.website);

      setStatus('LOADING');
      setEmail(fields.email);
      setFormError(null);

      triggerSignup(fields);
    });

    const handleErrorResponse = (response) => {
      if (response.fields) {
        if (response.fields.user_name) {
          setFormError(userAccountExistsErrorLabel);
        } else if (response.fields.account_name) {
          setFormError(accountNameExistsErrorLabel);
        } else if (response.fields.email_address) {
          setFormError('Email address already taken. Please use a different email address.');
        } else {
          setFormError(l10n.formatText('common.internal_server_error'));
        }
      } else {
        setFormError(l10n.formatText('common.internal_server_error'));
      }
      window.scrollTo(0, 0);
    };

    const passwordRules = new Map(
      [
        'at_least_1_uppercase',
        'at_least_1_lowercase',
        'at_least_8_characters',
        'at_least_1_special',
        'at_least_1_number',
      ].map((rule) => [rule, l10n.formatText(`pz.password.rules.${rule}`)]),
    );

    const usernameRules = new Map(
      [
        'only_alphanumeric',
        'must_be_unique',
        'at_least_2_characters',
        'at_most_32_characters',
        'cannot_contain_accountname',
      ].map((rule) => [rule, l10n.formatText(`pz.username.rules.${rule}`)]),
    );

    const accountNameRules = new Map(
      [
        'only_alphanumeric',
        'at_least_2_characters',
        'at_most_32_characters',
        'cannot_contain_username',
        'must_be_unique',
      ].map((rule) => [rule, l10n.formatText(`pz.account_name.rules.${rule}`)]),
    );

    const registerOptions = {
      username: {
        required: usernameMissingErrorLabel,
        minLength: {
          value: 3,
          message: usernameRules.get('at_least_2_characters'),
        },
        maxLength: {
          value: 32,
          message: usernameRules.get('at_most_32_characters'),
        },
        pattern: {
          value: /^\w[0-9a-zA-Z]+$/,
          message: usernameRules.get('only_alphanumeric'),
        },
      },
      accountName: {
        required: accountNameMissingErrorLabel,
        minLength: {
          value: 3,
          message: accountNameRules.get('at_least_2_characters'),
        },
        maxLength: {
          value: 32,
          message: accountNameRules.get('at_most_32_characters'),
        },
        pattern: {
          value: /^\w[0-9a-zA-Z]+$/,
          message: accountNameRules.get('only_alphanumeric'),
        },
        validate: {
          userNameInAccountNameRule: (value) =>
            watch('username') === '' ||
            !value.includes(watch('username')) ||
            accountNameRules.get('cannot_contain_username'),
        },
      },
      password: {
        required: passwordMissingErrorLabel,
        minLength: {
          value: 8,
          message: passwordRules.get('at_least_8_characters'),
        },
        validate: {
          upperCase: (value) => value.toLowerCase() !== value || passwordRules.get('at_least_1_uppercase'),
          lowerCase: (value) => value.toUpperCase() !== value || passwordRules.get('at_least_1_lowercase'),
          hasNumber: (value) => /\d/.test(value) || passwordRules.get('at_least_1_number'),
          hasSpecialChar: (value) =>
            /[`!@#$%^&*()_+\-=\]{};':"\\|,.<>?~]/.test(value) || passwordRules.get('at_least_1_special'),
        },
      },
      confirmPassword: {
        required: confirmPasswordMissingErrorLabel,
        validate: (value) => value === watch('password') || confirmPasswordMismatchLabel,
      },
      email: {
        required: emailMissingErrorLabel,
        pattern: {
          value:
            /^[a-zA-Z0-9_\.\-\+](?:[a-z0-9!#$%&'*+/=?^_`{|}~-]*(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i,
          message: emailInvalidErrorLabel,
        },
      },
      address: {
        required: addressMissingErrorLabel,
      },
      postcode: {
        required: postcodeMissingErrorLabel,
      },
      phone: {
        required: phoneMissingErrorlabel,
      },
      country: {
        required: countryMissingErrorLabel,
      },
      termsandcondition: {
        required: pzTermsAndConditionsMissingErrorLabel,
      },
    };

    const currentEmail = userAuth?.currentUser.email;
    let currentCountry = userAuth?.currentUserDetails.country;
    currentCountry = countries?.has(currentCountry) ? currentCountry : null;

    React.useEffect(() => {
      pzStore.fetchTerms(currentCountry, locale);
    }, []);

    if (termsState === 'error') {
      return (
        <UitkSpacing margin={{ blockstart: 'three' }}>
          <div className="SignupPz__error">
            <UitkSpacingHr />
            <UitkEmptyState>
              <UitkIcon name="sentiment_2" size={UitkIconSize.LARGE} />
              <UitkHeading size={5} tag="h4">
                We're having trouble loading the form
              </UitkHeading>
              <UitkText size={300}>Click below to reload the page or try again later.</UitkText>
              <UitkEmptyStateActionArea>
                <UitkTertiaryButton name="textonly" type="button" onClick={() => window.location.reload()}>
                  Reload
                </UitkTertiaryButton>
              </UitkEmptyStateActionArea>
            </UitkEmptyState>
          </div>
        </UitkSpacing>
      );
    }

    return (
      <>
        <form name={'pzSignupForm'} onSubmit={onSubmit} method="POST" className="SignupPz__form">
          {formError && (
            <UitkSpacing margin={{ blockend: 'six' }}>
              <div>
                <UitkErrorSummary heading={formError} />
              </div>
            </UitkSpacing>
          )}
          <UitkLayoutFlex direction={'column'} space="three">
            <UitkLayoutFlexItem>
              <div>
                <UitkHeading tag="h4" size={5}>
                  <LocalizedText message="pz.signup.form.create_your_account" />
                </UitkHeading>
                <UitkText inline={true} size={300}>
                  <LocalizedText message="pz.signup.form.partners_must_meet_eligibility" />{' '}
                  <UitkLink inline={true} onClick={() => setInfoEntry('eligibility')}>
                    <Link
                      to={{
                        pathname: location.pathname,
                        search: mergeQueryString(location.search, {
                          pwaDialog: 'pzFormInfo',
                        }),
                      }}
                      role="button"
                    >
                      <LocalizedText message="pz.signup.form.eligibility.learn_more" />
                    </Link>
                  </UitkLink>
                </UitkText>
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <div>
                <UitkSpacing margin={{ block: 'two' }}>
                  <UitkMoreInfoTrigger onClick={() => setInfoEntry('username')}>
                    <Link
                      to={{
                        pathname: location.pathname,
                        search: mergeQueryString(location.search, {
                          pwaDialog: 'pzFormInfo',
                        }),
                      }}
                      role="button"
                    >
                      <UitkText size={300}>{pzSignupUsername}</UitkText>
                      <UitkIcon name="info_outline" />
                    </Link>
                  </UitkMoreInfoTrigger>
                </UitkSpacing>
                <Controller
                  control={control}
                  name="username"
                  rules={registerOptions.username}
                  render={({ field: { onChange, onBlur, value, name } }) => (
                    <UitkTextInput
                      id="signUpFormUsernameInput"
                      name={name}
                      label={pzSignupUsername}
                      value={value}
                      onChange={({ target: { value: newValue } }) => {
                        onChange(newValue);
                      }}
                      onBlur={onBlur}
                      data-cy="sign-up-username-field"
                      invalid={errors.username?.message}
                    />
                  )}
                />
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <div>
                <UitkSpacing margin={{ block: 'two' }}>
                  <UitkMoreInfoTrigger onClick={() => setInfoEntry('password')}>
                    <Link
                      to={{
                        pathname: location.pathname,
                        search: mergeQueryString(location.search, {
                          pwaDialog: 'pzFormInfo',
                        }),
                      }}
                      role="button"
                    >
                      <UitkText size={300}>{pzSignupPassword}</UitkText>
                      <UitkIcon name="info_outline" />
                    </Link>
                  </UitkMoreInfoTrigger>
                </UitkSpacing>
                <Controller
                  control={control}
                  name="password"
                  rules={registerOptions.password}
                  render={({ field: { onChange, onBlur, value, name } }) => (
                    <UitkPasswordInput
                      id="passwordInput"
                      label={pzSignupPassword}
                      name={name}
                      value={value}
                      onChange={({ target: { value: passwordValue } }) => {
                        onChange(passwordValue);
                      }}
                      onBlur={onBlur}
                      invalid={errors.password?.message}
                      data-cy="sign-up-password-field"
                    />
                  )}
                />
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <Controller
                control={control}
                name="confirmPassword"
                rules={registerOptions.confirmPassword}
                render={({ field: { onChange, onBlur, value, name } }) => (
                  <UitkPasswordInput
                    id="confirmPasswordInput"
                    label={confirmPasswordLabel}
                    name={name}
                    value={value}
                    onChange={({ target: { value: confirmPasswordValue } }) => {
                      onChange(confirmPasswordValue);
                    }}
                    onBlur={onBlur}
                    invalid={errors.confirmPassword?.message}
                    data-cy="sign-up-password-field"
                  />
                )}
              />
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <div>
                <UitkSpacing margin={{ block: 'two' }}>
                  <UitkMoreInfoTrigger onClick={() => setInfoEntry('accountname')}>
                    <Link
                      to={{
                        pathname: location.pathname,
                        search: mergeQueryString(location.search, {
                          pwaDialog: 'pzFormInfo',
                        }),
                      }}
                      role="button"
                    >
                      <UitkText size={300}>{pzSignupAccountName}</UitkText>
                      <UitkIcon name="info_outline" />
                    </Link>
                  </UitkMoreInfoTrigger>
                </UitkSpacing>
                <Controller
                  control={control}
                  name="accountName"
                  rules={registerOptions.accountName}
                  render={({ field: { onChange, onBlur, value, name } }) => (
                    <UitkTextInput
                      id="signUpFormAccountNameInput"
                      name={name}
                      label={pzSignupAccountName}
                      value={value}
                      onChange={({ target: { value: newValue } }) => {
                        onChange(newValue);
                      }}
                      onBlur={onBlur}
                      data-cy="sign-up-accountname-field"
                      invalid={errors.accountName?.message}
                    />
                  )}
                />
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <Controller
                control={control}
                name="email"
                defaultValue={currentEmail}
                rules={registerOptions.email}
                render={({ field: { onChange, onBlur, value, name } }) => (
                  <UitkEmailInput
                    id="signUpFormEmailInput"
                    name={name}
                    label={emailLabel}
                    value={value}
                    onChange={({ target: { value: newValue } }) => {
                      onChange(newValue);
                    }}
                    onBlur={onBlur}
                    invalid={errors.email?.message}
                    data-cy="sign-up-email-field"
                  />
                )}
              />
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <Controller
                control={control}
                name="address1"
                rules={registerOptions.address}
                render={({ field: { onChange, onBlur, value, name } }) => (
                  <UitkTextInput
                    id="signUpFormAddressInput"
                    name={name}
                    label={pzSignupAddress}
                    value={value}
                    onChange={({ target: { value: newValue } }) => {
                      onChange(newValue);
                    }}
                    onBlur={onBlur}
                    invalid={errors.address1?.message}
                    data-cy="sign-up-address-1-field"
                  />
                )}
              />
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <Controller
                control={control}
                name="postcode"
                rules={registerOptions.postcode}
                render={({ field: { onChange, onBlur, value, name } }) => (
                  <UitkTextInput
                    id="signUpFormPostcodeInput"
                    name={name}
                    label={pzSignupPostcode}
                    value={value}
                    onChange={({ target: { value: newValue } }) => {
                      onChange(newValue);
                    }}
                    onBlur={onBlur}
                    invalid={errors.postcode?.message}
                    data-cy="sign-up-postcode-field"
                  />
                )}
              />
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <Controller
                control={control}
                name="phone"
                rules={registerOptions.phone}
                render={({ field: { onChange, onBlur, value, name } }) => (
                  <UitkTextInput
                    id="signUpFormPhoneInput"
                    name={name}
                    label={pzSignupPhone}
                    value={value}
                    onChange={({ target: { value: newValue } }) => {
                      onChange(newValue);
                    }}
                    onBlur={onBlur}
                    invalid={errors.phone?.message}
                    data-cy="sign-up-phone-field"
                  />
                )}
              />
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <Controller
                control={control}
                defaultValue={currentCountry}
                name="country"
                rules={registerOptions.country}
                render={({ field: { onChange, onBlur, value, name } }) => (
                  <UitkSelect
                    id="signUpFormCountryInput"
                    name={name}
                    label={pzCountryLabel}
                    value={value}
                    onChange={({ target: { value: newValue } }) => {
                      pzStore.fetchTerms(newValue, locale);
                      setCheckboxValue({ ...checkboxValue, pzTc: false });
                      onChange(newValue);
                    }}
                    onBlur={onBlur}
                    emptyState={true}
                    invalid={errors.country?.message}
                    data-cy="sign-up-country-field"
                  >
                    {[...countries].map((item, i) => {
                      return (
                        <option key={i} value={item[0]}>
                          {item[1]}
                        </option>
                      );
                    })}
                  </UitkSelect>
                )}
              />
            </UitkLayoutFlexItem>
            <UitkSpacing margin={{ blockstart: 'four', blockend: 'three' }}>
              <div>
                <UitkSpacing margin={{ blockstart: 'four', blockend: 'three' }}>
                  <UitkHeading tag="h4" size={5}>
                    <LocalizedText message="pz.signup.form.terms_and_conditions.title.review_our_terms_and_conditions" />
                  </UitkHeading>
                </UitkSpacing>
                <UitkCheckboxGroup group="checkboxGroup">
                  <Controller
                    control={control}
                    name="pzSignupPzTc"
                    defaultValue={checkboxValue.pzTc}
                    rules={registerOptions.termsandcondition}
                    render={({ field: { onChange, value, name } }) => (
                      <div className={`PzCheckbox ${termsState === 'pending' ? 'PzCheckbox--pending' : ''}`}>
                        <UitkLoader isSmall={true} className="PzCheckbox__spinner" />
                        <UitkCheckbox
                          id="pzSignupPzTc"
                          name={name}
                          label={pzTermsAndConditionsLabel}
                          checked={checkboxValue.pzTc}
                          disabled={!(terms?.length > 0)}
                          onChange={({ target: { checked } }) => {
                            onChange(checked);
                            setCheckboxValue({ ...checkboxValue, pzTc: checked });
                          }}
                          errorMessage={errors.pzSignupPzTc?.message}
                        >
                          <LocalizedTextWrapping
                            message="pz.signup.form.terms_and_conditions.i_have_read_and_accept_the_terms"
                            fieldName="termsAndConditions"
                          >
                            <UitkLink inline={true}>
                              <Link
                                to={{
                                  pathname: location.pathname,
                                  search: mergeQueryString(location.search, {
                                    pwaDialog: 'pzTerms',
                                  }),
                                }}
                                role="button"
                              >
                                <LocalizedText message="pz.signup.form.terms_and_conditions.pz_terms" />
                              </Link>
                            </UitkLink>
                          </LocalizedTextWrapping>
                        </UitkCheckbox>
                      </div>
                    )}
                  />
                  <Controller
                    control={control}
                    name="pzSignupProgramTc"
                    defaultValue={false}
                    rules={registerOptions.termsandcondition}
                    render={({ field: { onChange, value, name } }) => (
                      <div
                        className={`PzCheckbox ${
                          pzCampaignStore?.campaignState === 'pending' ? 'PzCheckbox--pending' : ''
                        }`}
                      >
                        <UitkLoader isSmall={true} className="PzCheckbox__spinner" />
                        <UitkCheckbox
                          id="pzSignupProgramTc"
                          data-stid="pzSignupProgramTc"
                          name={name}
                          label={programTermsAndConditionsLabel}
                          checked={value}
                          disabled={!(pzCampaignStore?.campaignTerms?.length > 0)}
                          onChange={({ target: { checked } }) => {
                            onChange(checked);
                            setCheckboxValue({ ...checkboxValue, programTc: checked });
                          }}
                          errorMessage={errors.pzSignupProgramTc?.message}
                        >
                          <LocalizedTextWrapping
                            message="pz.signup.form.terms_and_conditions.i_have_read_and_accept_the_program_terms"
                            fieldName="termsAndConditions"
                          >
                            <UitkLink inline={true}>
                              <Link
                                to={{
                                  pathname: `/${locale}/entity-terms-and-conditions`,
                                  search: `?programName=${program.name}`,
                                }}
                                target={'_blank'}
                              >
                                <LocalizedText
                                  message="pz.signup.form.terms_and_conditions.program_terms"
                                  data={[program.title]}
                                />
                              </Link>
                            </UitkLink>
                          </LocalizedTextWrapping>
                        </UitkCheckbox>
                      </div>
                    )}
                  />
                </UitkCheckboxGroup>
              </div>
            </UitkSpacing>
            <UitkLayoutFlexItem>
              <UitkPrimaryButton
                data-cy="sign-up-submit"
                id="signUpFormSubmitButton"
                type="submit"
                className={'spacing-top-bottom-24'}
                disabled={errors.length > 0 || status === 'LOADING'}
                size={UitkButtonSize.LARGE}
              >
                {status === 'LOADING' ? <UitkLoader isSmall={true} isDarkBackground={true} /> : pzSignupSubmitLabel}
              </UitkPrimaryButton>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </form>
        <PzFormInfoDialog
          dialogId="pzFormInfo"
          infoEntry={infoEntry}
          l10n={l10n}
          programName={program.title}
          rules={{
            username: Array.from(usernameRules.values()),
            password: Array.from(passwordRules.values()),
            accountname: Array.from(accountNameRules.values()),
          }}
        />
      </>
    );
  },
);
